import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/portfolio/Navbar";
import { 
  ArrowLeft, 
  ArrowUpRight, 
  Building2, 
  Layers, 
  Globe, 
  Calendar, 
  User, 
  CheckCircle2, 
  ArrowRight, 
  TrendingUp, 
  Share2, 
  AlertTriangle, 
  Sparkles, 
  ShieldCheck, 
  Clock, 
  Check, 
  Zap, 
  BarChart3, 
  GraduationCap, 
  Mail, 
  Users, 
  Search, 
  Workflow, 
  BookOpen, 
  HelpCircle, 
  MessageSquare, 
  Award, 
  Compass, 
  ChevronRight,
  Send
} from "lucide-react";

export default function WorkhoursCaseStudyPage() {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const originalTitle = document.title;
    const bestTitle = "Workhours (Trydo School) Case Study: Transforming SEO Traffic to Student Growth | Kanna Narendra Kumar";
    const bestDesc = "How Kanna Narendra Kumar scaled Workhours (now Trydo School)—slashing refund ratios by 60%, boosting course-intent traffic by 35%, and driving email automation & mentorship.";

    document.title = bestTitle;

    // Helper to set meta tags
    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (el) {
        el.setAttribute(attr, value);
      } else {
        const meta = document.createElement("meta");
        const isProp = selector.includes("property=");
        if (isProp) {
          const match = selector.match(/property="([^"]+)"/);
          if (match) meta.setAttribute("property", match[1]);
        } else {
          const match = selector.match(/name="([^"]+)"/);
          if (match) meta.setAttribute("name", match[1]);
        }
        meta.setAttribute(attr, value);
        document.head.appendChild(meta);
      }
    };

    // Set Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    const originalDesc = metaDesc ? metaDesc.getAttribute("content") : "";
    setMeta('meta[name="description"]', "content", bestDesc);

    // Set Social / OG Meta Tags
    setMeta('meta[property="og:title"]', "content", bestTitle);
    setMeta('meta[property="og:description"]', "content", bestDesc);
    setMeta('meta[property="og:url"]', "content", "https://narendradigital.in/casestudy/workhours/");
    setMeta('meta[property="og:type"]', "content", "article");
    setMeta('meta[name="twitter:title"]', "content", bestTitle);
    setMeta('meta[name="twitter:description"]', "content", bestDesc);

    // Set Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    let createdCanonical = false;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
      createdCanonical = true;
    }
    const originalCanonical = canonical ? canonical.getAttribute("href") : null;
    if (canonical) {
      canonical.setAttribute("href", "https://narendradigital.in/casestudy/workhours/");
    }

    // Add JSON-LD Schema
    const scriptId = "workhours-case-study-schema";
    let scriptTag = document.getElementById(scriptId);
    if (!scriptTag) {
      scriptTag = document.createElement("script");
      scriptTag.id = scriptId;
      scriptTag.type = "application/ld+json";
      scriptTag.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": "Workhours (Trydo School) Case Study: Transforming SEO Traffic to Student Growth",
        "description": bestDesc,
        "author": {
          "@type": "Person",
          "name": "Kanna Narendra Kumar",
          "jobTitle": "Senior SEO Specialist & AI Search Strategist",
          "url": "https://narendradigital.in/"
        },
        "publisher": {
          "@type": "Person",
          "name": "Kanna Narendra Kumar"
        },
        "mainEntityOfPage": "https://narendradigital.in/casestudy/workhours/"
      });
      document.head.appendChild(scriptTag);
    }

    window.scrollTo(0, 0);

    return () => {
      document.title = originalTitle;
      if (metaDesc && originalDesc) metaDesc.setAttribute("content", originalDesc);
      if (canonical) {
        if (createdCanonical) {
          canonical.remove();
        } else if (originalCanonical) {
          canonical.setAttribute("href", originalCanonical);
        }
      }
      if (scriptTag) scriptTag.remove();
    };
  }, []);

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <div className="bg-[#080c16] text-[#e2e8f0] min-h-screen font-sans selection:bg-[#3b82f6] selection:text-white antialiased relative overflow-x-hidden">
      
      {/* Main Global Navigation Bar */}
      <Navbar />

      {/* Ambient background lighting */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[850px] h-[450px] bg-gradient-to-b from-indigo-600/15 via-blue-500/10 to-transparent blur-[120px] -z-10" />
      <div className="pointer-events-none absolute top-[900px] -left-48 w-[500px] h-[500px] bg-purple-600/10 blur-[140px] -z-10" />
      <div className="pointer-events-none absolute top-[2400px] -right-48 w-[500px] h-[500px] bg-emerald-600/10 blur-[140px] -z-10" />

      {/* Main Container */}
      <main className="max-w-4xl mx-auto px-5 sm:px-8 pt-28 sm:pt-32 pb-16">

        {/* Back Link & Share Utility Bar */}
        <div className="flex items-center justify-between gap-4 mb-8 pb-3 border-b border-white/[0.08]">
          <Link
            to="/#case-studies"
            className="group inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-1 text-cyan-400" />
            <span>Back to Case Studies</span>
          </Link>

          <div className="flex items-center gap-3 text-xs font-mono">
            <button
              onClick={handleShare}
              className="px-2.5 py-1 rounded-md border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-slate-300 hover:text-white transition-colors flex items-center gap-1.5 cursor-pointer"
              title="Copy Case Study URL"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-emerald-400">Copied Link</span>
                </>
              ) : (
                <>
                  <Share2 className="w-3.5 h-3.5" />
                  <span>Share</span>
                </>
              )}
            </button>
            <a
              href="mailto:narendrakumarkanna@gmail.com"
              className="text-cyan-400 hover:text-white transition-colors flex items-center gap-1 font-semibold"
            >
              <span>Contact</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        {/* HERO SECTION */}
        <section className="mb-12">
          
          {/* Tag Badges */}
          <div className="flex flex-wrap items-center gap-2 text-xs font-mono text-slate-400 mb-6">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 font-semibold shadow-inner">
              <Building2 className="w-3.5 h-3.5 text-blue-400" />
              <span>Workhours → Trydo School</span>
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 font-semibold">
              <Search className="w-3.5 h-3.5 text-cyan-400" />
              <span>SEO · Digital Marketing</span>
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 font-semibold">
              <GraduationCap className="w-3.5 h-3.5 text-purple-400" />
              <span>Student Mentorship</span>
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 font-semibold">
              <Mail className="w-3.5 h-3.5 text-emerald-400" />
              <span>ConvertKit Automation</span>
            </div>
          </div>

          {/* Main Title */}
          <h1 className="font-heading font-extrabold text-3xl sm:text-5xl lg:text-[44px] text-white tracking-tight leading-[1.2] mb-5">
            From SEO Traffic to Student Growth:{" "}
            <span className="bg-gradient-to-r from-blue-400 via-indigo-300 to-cyan-300 bg-clip-text text-transparent">
              Transforming Workhours
            </span>{" "}
            <span className="text-white/60 text-2xl sm:text-4xl font-semibold">(Now Trydo School)</span>
          </h1>

          {/* Subtitle */}
          <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-3xl mb-6">
            How I helped scale an online digital marketing institute by rebuilding the SEO foundation, mentoring students in live masterclasses, creating ConvertKit lifecycle automations, and cutting refund ratios by 60%.
          </p>

          {/* Rebranding Notice Callout */}
          <div className="p-4 sm:p-4.5 rounded-xl bg-blue-950/30 border border-blue-500/30 text-xs sm:text-sm text-blue-200/90 leading-relaxed mb-8 flex items-start gap-3">
            <span className="px-2 py-0.5 rounded bg-blue-500/20 border border-blue-400/30 text-blue-300 font-mono text-[11px] uppercase font-bold flex-shrink-0 mt-0.5">
              Rebrand Note
            </span>
            <p>
              Workhours has since rebranded as <strong className="text-white font-semibold">Trydo School</strong>. This case study reflects my hands-on responsibilities, technical architectures, and mentorship programs during my tenure at Workhours.
            </p>
          </div>


          {/* Author Card */}
          <div className="p-4 sm:p-5 rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-sm shadow-xl flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3.5">
              <div className="relative">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 via-indigo-600 to-cyan-400 text-white flex items-center justify-center font-bold text-sm shadow-md shadow-blue-500/20">
                  KN
                </div>
                <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-emerald-400 border-2 border-[#080c16] rounded-full" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-white font-semibold text-sm">Kanna Narendra Kumar</p>
                  <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-cyan-400/10 text-cyan-300 border border-cyan-400/20">Growth &amp; Mentor Lead</span>
                </div>
                <p className="text-xs text-slate-400">SEO Strategist · Digital Marketing Educator</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs font-mono text-slate-400 pt-3 sm:pt-0 border-t sm:border-t-0 border-white/10">
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-slate-500" />
                <span>6 min read</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5 text-slate-500" />
                <span>EdTech &amp; Digital Education</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-emerald-400 font-medium">Verified Results</span>
              </div>
            </div>
          </div>
        </section>

        {/* 3 KEY METRICS CARDS */}
        <section className="mb-14">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            
            {/* Metric 1 */}
            <div className="p-5 rounded-2xl bg-gradient-to-b from-white/[0.05] to-white/[0.01] border border-white/10 hover:border-emerald-500/40 transition-all duration-300">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400 font-medium">Refund Ratio</span>
                <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center">
                  <ShieldCheck className="w-3.5 h-3.5" />
                </div>
              </div>
              <div className="font-heading font-extrabold text-3xl text-emerald-400 tracking-tight">
                -60%
              </div>
              <p className="text-xs text-slate-400 mt-1.5 leading-snug">
                Structured student support and dedicated doubt resolution drastically lowered course refunds.
              </p>
              <div className="mt-3 pt-2.5 border-t border-white/5 flex items-center gap-1 text-[11px] font-mono text-emerald-400">
                <CheckCircle2 className="w-3 h-3" />
                <span>Drastic Student Retention</span>
              </div>
            </div>

            {/* Metric 2 */}
            <div className="p-5 rounded-2xl bg-gradient-to-b from-white/[0.05] to-white/[0.01] border border-white/10 hover:border-cyan-500/40 transition-all duration-300">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400 font-medium">High-Intent Traffic</span>
                <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 flex items-center justify-center">
                  <TrendingUp className="w-3.5 h-3.5" />
                </div>
              </div>
              <div className="font-heading font-extrabold text-3xl text-cyan-400 tracking-tight">
                +35%
              </div>
              <p className="text-xs text-slate-400 mt-1.5 leading-snug">
                Year-over-year surge in organic search traffic specifically from course- and learning-intent keywords.
              </p>
              <div className="mt-3 pt-2.5 border-t border-white/5 flex items-center gap-1 text-[11px] font-mono text-cyan-400">
                <Search className="w-3 h-3" />
                <span>Course-Seeking Visitors</span>
              </div>
            </div>

            {/* Metric 3 */}
            <div className="p-5 rounded-2xl bg-gradient-to-b from-white/[0.05] to-white/[0.01] border border-white/10 hover:border-indigo-500/40 transition-all duration-300">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400 font-medium">Teaching &amp; Mentorship</span>
                <div className="w-7 h-7 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 flex items-center justify-center">
                  <GraduationCap className="w-3.5 h-3.5" />
                </div>
              </div>
              <div className="font-heading font-extrabold text-3xl text-indigo-400 tracking-tight">
                2 Classes / Wk
              </div>
              <p className="text-xs text-slate-400 mt-1.5 leading-snug">
                Conducted live interactive masterclasses and 1-on-1 practical doubt clearing for over 100+ students.
              </p>
              <div className="mt-3 pt-2.5 border-t border-white/5 flex items-center gap-1 text-[11px] font-mono text-indigo-400">
                <Users className="w-3 h-3" />
                <span>100+ Students Mentored</span>
              </div>
            </div>

          </div>
        </section>

        {/* SECTION 1: OVERVIEW & SCALING BOTTLENECK */}
        <section id="overview" className="scroll-mt-24 mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold">
              01 / Overview &amp; Context
            </h2>
          </div>

          <h3 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-4">
            An EdTech Mission with a Single-Point Bottleneck
          </h3>

          <div className="space-y-4 text-base leading-relaxed text-slate-300 mb-6">
            <p>
              <strong className="text-white font-semibold">Workhours (now rebranded as Trydo School)</strong> is an online training institute focused on Digital Marketing, SEO, Affiliate Marketing, and digital skill-building, with a core mission of helping students build practical capabilities and achieve financial independence.
            </p>
            <p>
              The institute was founded and led by <strong className="text-white font-semibold">Veerander Chowdary (Hello Veeru)</strong>, a visionary educator deeply involved in training students and building the community.
            </p>
          </div>

          {/* Operational Bottleneck Highlight */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent border border-amber-500/30 p-6 sm:p-7 shadow-lg mb-8">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/30 text-amber-300 flex items-center justify-center flex-shrink-0 mt-0.5">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div className="space-y-2">
                <span className="text-xs font-mono uppercase tracking-wider text-amber-400 font-bold px-2 py-0.5 rounded bg-amber-400/10 border border-amber-400/20 inline-block">
                  The Operational Challenge
                </span>
                <h4 className="font-heading font-bold text-lg sm:text-xl text-white">
                  Founder Overload: Running Everything Single-Handedly
                </h4>
                <p className="text-sm sm:text-base text-amber-100/90 leading-relaxed">
                  As the student base expanded, it became impossible for one person to oversee everything: training cohorts, curriculum design, student doubts, technical SEO, marketing content, email nurture sequences, and cross-team coordination.
                </p>
              </div>
            </div>
          </div>

          <p className="text-base text-slate-300 leading-relaxed">
            I stepped in to take complete ownership of these core areas—building a repeatable, scalable system across <strong className="text-cyan-300 font-medium">technical SEO, digital marketing education, student support, automated email journeys, and team leadership</strong>.
          </p>
        </section>

        {/* SECTION 2: THE CHALLENGE */}
        <section id="challenge" className="scroll-mt-24 mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold">
              02 / The Challenge
            </h2>
          </div>

          <h3 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-4">
            Traffic Without Course Intent &amp; Strained Operations
          </h3>
          <p className="text-base text-slate-300 leading-relaxed mb-6">
            The website was receiving organic search traffic, but a substantial volume came from <strong className="text-white font-medium">generic informational searches</strong> rather than prospective students actively looking to learn digital marketing or join a structured training program.
          </p>

          {/* 7 Challenge Breakdown Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            
            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-white/20 transition-all">
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center font-mono text-xs font-bold mb-3">
                01
              </div>
              <h4 className="font-heading font-bold text-base text-white mb-1.5">
                Misaligned Search Intent
              </h4>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                Organic traffic was broad and unfocused. Visitors landed on peripheral topics rather than commercial, high-intent queries tied to enrolling in courses.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-white/20 transition-all">
              <div className="w-8 h-8 rounded-lg bg-cyan-500/10 text-cyan-400 flex items-center justify-center font-mono text-xs font-bold mb-3">
                02
              </div>
              <h4 className="font-heading font-bold text-base text-white mb-1.5">
                Zero Dedicated SEO &amp; Content Team
              </h4>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                No formal team structure existed to produce optimized content, manage keyword research, or execute technical recommendations continuously.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-white/20 transition-all">
              <div className="w-8 h-8 rounded-lg bg-indigo-500/10 text-indigo-400 flex items-center justify-center font-mono text-xs font-bold mb-3">
                03
              </div>
              <h4 className="font-heading font-bold text-base text-white mb-1.5">
                Unstructured Curriculum
              </h4>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                Learning topics required better modularity and logical progression so students could easily follow and implement concepts in real time.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-white/20 transition-all">
              <div className="w-8 h-8 rounded-lg bg-purple-500/10 text-purple-400 flex items-center justify-center font-mono text-xs font-bold mb-3">
                04
              </div>
              <h4 className="font-heading font-bold text-base text-white mb-1.5">
                Student Support Gaps
              </h4>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                Students needed reliable, accessible mentoring for technical doubts. Without it, confusion led to frustration and heightened refund requests.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-white/20 transition-all">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center font-mono text-xs font-bold mb-3">
                05
              </div>
              <h4 className="font-heading font-bold text-base text-white mb-1.5">
                Fragmented Email Automation
              </h4>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                Email communications lacked behavioral triggers and clear journeys based on user actions, leaving valuable student leads un-nurtured.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-white/20 transition-all">
              <div className="w-8 h-8 rounded-lg bg-rose-500/10 text-rose-400 flex items-center justify-center font-mono text-xs font-bold mb-3">
                06
              </div>
              <h4 className="font-heading font-bold text-base text-white mb-1.5">
                Ad-Hoc Social Content Direction
              </h4>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                The Hello Veeru brand lacked structured educational concepts, creative briefs, and coordination with graphic designers.
              </p>
            </div>

          </div>
        </section>

        {/* SECTION 3: WHAT I IMPLEMENTED (6 PILLARS) */}
        <section id="implementation" className="scroll-mt-24 mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold">
              03 / What I Implemented
            </h2>
          </div>

          <h3 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-2">
            The 6 Execution Pillars
          </h3>
          <p className="text-base text-slate-300 leading-relaxed mb-8">
            I built a multi-dimensional operating system uniting technical SEO, content generation, curriculum design, live student mentorship, email automation, and brand creative direction.
          </p>

          <div className="space-y-6">
            
            {/* PILLAR 1 */}
            <div className="p-6 sm:p-7 rounded-2xl bg-[#0c1220] border border-white/10 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <span className="px-3 py-1 rounded-full bg-blue-500/15 border border-blue-500/30 text-blue-300 font-mono text-xs font-bold">
                  Pillar 01
                </span>
                <span className="text-xs font-mono text-slate-400">Technical &amp; On-Page SEO</span>
              </div>
              <h4 className="font-heading font-bold text-lg sm:text-xl text-white mb-3">
                Rebuilding the SEO Foundation
              </h4>
              <p className="text-sm text-slate-300 leading-relaxed mb-4">
                I initiated a thorough technical SEO audit of the platform, identifying crawl blockers and content deficiencies that restricted organic indexing. Working alongside developers, we systematically resolved:
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-mono text-slate-300">
                <div className="p-2.5 rounded-lg bg-white/[0.03] border border-white/5 flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                  <span>Technical SEO</span>
                </div>
                <div className="p-2.5 rounded-lg bg-white/[0.03] border border-white/5 flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                  <span>Crawl &amp; Indexing</span>
                </div>
                <div className="p-2.5 rounded-lg bg-white/[0.03] border border-white/5 flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                  <span>Site Architecture</span>
                </div>
                <div className="p-2.5 rounded-lg bg-white/[0.03] border border-white/5 flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                  <span>Internal Linking</span>
                </div>
                <div className="p-2.5 rounded-lg bg-white/[0.03] border border-white/5 flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                  <span>On-Page SEO</span>
                </div>
                <div className="p-2.5 rounded-lg bg-white/[0.03] border border-white/5 flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                  <span>Metadata Setup</span>
                </div>
                <div className="p-2.5 rounded-lg bg-white/[0.03] border border-white/5 flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                  <span>Content Pruning</span>
                </div>
                <div className="p-2.5 rounded-lg bg-white/[0.03] border border-white/5 flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                  <span>Keyword Mapping</span>
                </div>
              </div>
            </div>

            {/* PILLAR 2 */}
            <div className="p-6 sm:p-7 rounded-2xl bg-[#0c1220] border border-white/10 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <span className="px-3 py-1 rounded-full bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 font-mono text-xs font-bold">
                  Pillar 02
                </span>
                <span className="text-xs font-mono text-slate-400">Team Building &amp; Talent</span>
              </div>
              <h4 className="font-heading font-bold text-lg sm:text-xl text-white mb-3">
                Building an SEO &amp; Content Support Squad
              </h4>
              <p className="text-sm text-slate-300 leading-relaxed mb-4">
                To transform organic execution from a founder bottleneck into an autonomous engine, I hired and onboarded a dedicated <strong className="text-white font-medium">Content Writer</strong> and <strong className="text-white font-medium">SEO Intern</strong>.
              </p>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-start gap-2.5">
                  <ChevronRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Established clear Standard Operating Procedures (SOPs) for keyword research and search intent mapping.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <ChevronRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Created quality guidelines for content production, on-page optimization, and regular editorial calendars.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <ChevronRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Freed the founder completely from everyday content drafting and review cycles.</span>
                </li>
              </ul>
            </div>

            {/* PILLAR 3 */}
            <div className="p-6 sm:p-7 rounded-2xl bg-[#0c1220] border border-white/10 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <span className="px-3 py-1 rounded-full bg-purple-500/15 border border-purple-500/30 text-purple-300 font-mono text-xs font-bold">
                  Pillar 03
                </span>
                <span className="text-xs font-mono text-slate-400">Pedagogy &amp; Structure</span>
              </div>
              <h4 className="font-heading font-bold text-lg sm:text-xl text-white mb-3">
                Creating a Structured Learning Curriculum
              </h4>
              <p className="text-sm text-slate-300 leading-relaxed mb-4">
                I organized and refined the curriculum across four primary disciplines to make training practical, hands-on, and accessible:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div className="p-3.5 rounded-xl bg-white/[0.02] border border-white/5">
                  <div className="font-semibold text-white text-sm mb-1">Digital Marketing Fundamentals</div>
                  <p className="text-xs text-slate-400">Campaign architecture, conversion funnels, and marketing analytics.</p>
                </div>
                <div className="p-3.5 rounded-xl bg-white/[0.02] border border-white/5">
                  <div className="font-semibold text-white text-sm mb-1">Search Engine Optimization (SEO)</div>
                  <p className="text-xs text-slate-400">Keyword research, technical audits, content strategy, and ranking factors.</p>
                </div>
                <div className="p-3.5 rounded-xl bg-white/[0.02] border border-white/5">
                  <div className="font-semibold text-white text-sm mb-1">Social Media Marketing (SMM)</div>
                  <p className="text-xs text-slate-400">Community building, content calendars, engagement strategies, and branding.</p>
                </div>
                <div className="p-3.5 rounded-xl bg-white/[0.02] border border-white/5">
                  <div className="font-semibold text-white text-sm mb-1">Affiliate Marketing &amp; Monetization</div>
                  <p className="text-xs text-slate-400">Practical monetization models, commission funnels, and niche site creation.</p>
                </div>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Rather than treating topics in isolation, I designed modules that showed students how SEO, social media, and affiliate conversions work in tandem.
              </p>
            </div>

            {/* PILLAR 4 */}
            <div className="p-6 sm:p-7 rounded-2xl bg-[#0c1220] border border-white/10 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <span className="px-3 py-1 rounded-full bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 font-mono text-xs font-bold">
                  Pillar 04
                </span>
                <span className="text-xs font-mono text-slate-400">Teaching &amp; Student Success</span>
              </div>
              <h4 className="font-heading font-bold text-lg sm:text-xl text-white mb-3">
                Digital Marketing Mentor &amp; Student Support Lead
              </h4>
              <p className="text-sm text-slate-300 leading-relaxed mb-4">
                My role extended deeply into live education and mentorship. I conducted <strong className="text-white font-medium">two live masterclasses every week</strong> covering advanced SEO implementation, content frameworks, and social media campaigns.
              </p>
              
              <div className="p-4 rounded-xl bg-indigo-950/20 border border-indigo-500/20 mb-4">
                <p className="text-xs font-mono uppercase tracking-wider text-indigo-300 font-semibold mb-2">
                  Dedicated Student Mentorship Areas:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-300">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
                    <span>Real-time technical SEO troubleshooting</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
                    <span>Practical website &amp; blog critique</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
                    <span>Live social media campaign guidance</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
                    <span>Career roadmaps &amp; freelance guidance</span>
                  </div>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                By providing a dedicated, dependable point of contact for every student question, students felt genuinely supported. Crucially, this unlocked huge bandwidth for the founder to focus on high-impact business growth, advertising, and strategic partnerships.
              </p>
            </div>

            {/* PILLAR 5 */}
            <div className="p-6 sm:p-7 rounded-2xl bg-[#0c1220] border border-white/10 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <span className="px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 font-mono text-xs font-bold">
                  Pillar 05
                </span>
                <span className="text-xs font-mono text-slate-400">Lifecycle Marketing</span>
              </div>
              <h4 className="font-heading font-bold text-lg sm:text-xl text-white mb-3">
                Rebuilding Email Automation in ConvertKit
              </h4>
              <p className="text-sm text-slate-300 leading-relaxed mb-4">
                I redesigned our email marketing infrastructure from scratch, shifting from broadcast blasts to intelligent, <strong className="text-white font-medium">user-action and lifecycle-triggered automations</strong>.
              </p>
              
              <div className="p-4 rounded-xl bg-[#070c18] border border-white/5 space-y-3 mb-4">
                <div className="flex items-center gap-2 text-xs font-mono text-cyan-300">
                  <Workflow className="w-4 h-4" />
                  <span>Action-Based Sequence Architecture</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 text-xs text-slate-300">
                  <div className="p-2 rounded bg-white/[0.02] border border-white/5">
                    <strong className="block text-white">Event Triggers</strong>
                    <span className="text-[11px] text-slate-400">Lead capture, lesson completions, quiz attempts</span>
                  </div>
                  <div className="p-2 rounded bg-white/[0.02] border border-white/5">
                    <strong className="block text-white">Student Journey</strong>
                    <span className="text-[11px] text-slate-400">Onboarding, reminder sequences, re-engagement</span>
                  </div>
                  <div className="p-2 rounded bg-white/[0.02] border border-white/5">
                    <strong className="block text-white">Simple Copy</strong>
                    <span className="text-[11px] text-slate-400">Clean English, zero jargon, conversational tone</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Rewriting every sequence with clear CTAs and timely reminders lifted email click rates and ensured new enrollees hit their learning milestones on schedule.
              </p>
            </div>

            {/* PILLAR 6 */}
            <div className="p-6 sm:p-7 rounded-2xl bg-[#0c1220] border border-white/10 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <span className="px-3 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-300 font-mono text-xs font-bold">
                  Pillar 06
                </span>
                <span className="text-xs font-mono text-slate-400">Creative Direction &amp; Brand</span>
              </div>
              <h4 className="font-heading font-bold text-lg sm:text-xl text-white mb-3">
                Supporting the Social Media &amp; Creative Team
              </h4>
              <p className="text-sm text-slate-300 leading-relaxed mb-4">
                I partnered with the creative team for the personal and institutional <strong className="text-white font-medium">Hello Veeru</strong> brand, acting as the bridge between educational substance and visual design:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-slate-300">
                <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
                  <span className="text-rose-400 font-mono block mb-1">Concepts</span>
                  <p className="text-slate-400">Brainstormed high-resonance educational post ideas and real-world case studies.</p>
                </div>
                <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
                  <span className="text-rose-400 font-mono block mb-1">Collaboration</span>
                  <p className="text-slate-400">Drafted creative briefs and collaborated with graphic designers on visual layouts.</p>
                </div>
                <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5">
                  <span className="text-rose-400 font-mono block mb-1">Quality Review</span>
                  <p className="text-slate-400">Reviewed final copies and creatives to ensure technical accuracy and brand alignment.</p>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* SECTION 4: CROSS-FUNCTIONAL LEADERSHIP MATRIX */}
        <section id="role" className="scroll-mt-24 mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold">
              04 / Cross-Functional Matrix
            </h2>
          </div>

          <h3 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-3">
            My Role Across the Organization
          </h3>
          <p className="text-base text-slate-300 leading-relaxed mb-6">
            My responsibilities rapidly expanded beyond traditional SEO into comprehensive EdTech leadership and interdepartmental alignment.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 mb-8">
            
            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 hover:border-blue-500/30 transition-all">
              <div className="flex items-center gap-2 text-blue-400 mb-2 font-heading font-bold text-sm">
                <Search className="w-4 h-4" />
                <span>SEO &amp; Organic Growth</span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Technical SEO · Keyword Research · Content Strategy · Search Architecture
              </p>
            </div>

            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 hover:border-purple-500/30 transition-all">
              <div className="flex items-center gap-2 text-purple-400 mb-2 font-heading font-bold text-sm">
                <BookOpen className="w-4 h-4" />
                <span>Digital Marketing Education</span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                SEO · Digital Marketing · Social Media Marketing · Affiliate Marketing
              </p>
            </div>

            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 hover:border-cyan-500/30 transition-all">
              <div className="flex items-center gap-2 text-cyan-400 mb-2 font-heading font-bold text-sm">
                <HelpCircle className="w-4 h-4" />
                <span>Student Support &amp; Success</span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Live Classes · Doubt Resolution · Practical Guidance · Career Guidance
              </p>
            </div>

            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 hover:border-emerald-500/30 transition-all">
              <div className="flex items-center gap-2 text-emerald-400 mb-2 font-heading font-bold text-sm">
                <Workflow className="w-4 h-4" />
                <span>Marketing Automation</span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Email Journeys · ConvertKit · User-Based Automation · Onboarding
              </p>
            </div>

            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 hover:border-rose-500/30 transition-all">
              <div className="flex items-center gap-2 text-rose-400 mb-2 font-heading font-bold text-sm">
                <Sparkles className="w-4 h-4" />
                <span>Content &amp; Brand</span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Content Strategy · Social Media Ideas · Creative Direction · Brand Voice
              </p>
            </div>

            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 hover:border-indigo-500/30 transition-all">
              <div className="flex items-center gap-2 text-indigo-400 mb-2 font-heading font-bold text-sm">
                <Users className="w-4 h-4" />
                <span>Team Leadership</span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Development · Content Writers · SEO Interns · Graphic Designers
              </p>
            </div>

          </div>

          {/* Central Bridge Diagram */}
          <div className="p-5 rounded-2xl bg-gradient-to-r from-blue-950/40 via-indigo-950/30 to-purple-950/40 border border-indigo-500/20 text-center">
            <span className="text-[11px] font-mono uppercase tracking-widest text-indigo-300 font-semibold block mb-2">
              Cross-Functional Coordination Bridge
            </span>
            <div className="flex flex-wrap items-center justify-center gap-2 text-xs sm:text-sm font-semibold text-white">
              <span className="px-2.5 py-1 rounded-lg bg-white/10">Founder</span>
              <ArrowRight className="w-3.5 h-3.5 text-cyan-400" />
              <span className="px-2.5 py-1 rounded-lg bg-white/10">Development</span>
              <ArrowRight className="w-3.5 h-3.5 text-cyan-400" />
              <span className="px-2.5 py-1 rounded-lg bg-white/10">Content</span>
              <ArrowRight className="w-3.5 h-3.5 text-cyan-400" />
              <span className="px-2.5 py-1 rounded-lg bg-white/10">SEO</span>
              <ArrowRight className="w-3.5 h-3.5 text-cyan-400" />
              <span className="px-2.5 py-1 rounded-lg bg-white/10">Design</span>
              <ArrowRight className="w-3.5 h-3.5 text-cyan-400" />
              <span className="px-2.5 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">Students</span>
            </div>
            <p className="text-xs text-slate-400 mt-3 max-w-xl mx-auto">
              I effectively served as the central node connecting the founder's vision with hands-on technical execution, design delivery, and ongoing student satisfaction.
            </p>
          </div>
        </section>

        {/* SECTION 5: OUTCOMES */}
        <section id="outcomes" className="scroll-mt-24 mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold">
              05 / Business Outcomes &amp; Impact
            </h2>
          </div>

          <h3 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-6">
            Measurable Results Across Growth &amp; Operations
          </h3>

          <div className="space-y-4">
            
            <div className="p-5 sm:p-6 rounded-2xl bg-white/[0.02] border border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono uppercase text-emerald-400 font-bold">Key Achievement</span>
                  <span className="text-white/30">•</span>
                  <span className="text-xs text-slate-400">Student Retention</span>
                </div>
                <h4 className="font-heading font-bold text-lg text-white mb-1">
                  60% Reduction in Refund Ratio
                </h4>
                <p className="text-xs sm:text-sm text-slate-300 max-w-xl">
                  By instituting dedicated doubt-clearing sessions and giving students a dependable mentor for practical questions, student frustration vanished, leading to a direct 60% plunge in refund requests.
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <span className="font-heading font-black text-3xl sm:text-4xl text-emerald-400">-60%</span>
                <span className="block text-[10px] font-mono text-slate-400 uppercase">Refund Rate</span>
              </div>
            </div>

            <div className="p-5 sm:p-6 rounded-2xl bg-white/[0.02] border border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono uppercase text-cyan-400 font-bold">Key Achievement</span>
                  <span className="text-white/30">•</span>
                  <span className="text-xs text-slate-400">Organic Growth</span>
                </div>
                <h4 className="font-heading font-bold text-lg text-white mb-1">
                  35% Surge in Course-Intent Traffic
                </h4>
                <p className="text-xs sm:text-sm text-slate-300 max-w-xl">
                  Within one year, organic visibility pivoted from broad trivia searches to high-intent educational keywords. Visitors actively exploring training programs grew by 35%.
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <span className="font-heading font-black text-3xl sm:text-4xl text-cyan-400">+35%</span>
                <span className="block text-[10px] font-mono text-slate-400 uppercase">Intent Queries</span>
              </div>
            </div>

            <div className="p-5 sm:p-6 rounded-2xl bg-white/[0.02] border border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono uppercase text-indigo-400 font-bold">Key Achievement</span>
                  <span className="text-white/30">•</span>
                  <span className="text-xs text-slate-400">Founder Leverage</span>
                </div>
                <h4 className="font-heading font-bold text-lg text-white mb-1">
                  Drastically Reduced Founder Dependency
                </h4>
                <p className="text-xs sm:text-sm text-slate-300 max-w-xl">
                  By managing SEO, student doubts, curriculum updates, and email workflows, the founder was freed to dedicate energy to advertising scale, social reach, and high-level training.
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <span className="font-heading font-black text-3xl sm:text-4xl text-indigo-400">100%</span>
                <span className="block text-[10px] font-mono text-slate-400 uppercase">Ops Autonomous</span>
              </div>
            </div>

          </div>
        </section>

        {/* SECTION 6: THE TRANSFORMATION (BEFORE VS AFTER) */}
        <section id="transformation" className="scroll-mt-24 mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold">
              06 / The Transformation
            </h2>
          </div>

          <h3 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-6">
            From High Dependency to Scalable Operations
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* Before Card */}
            <div className="p-6 rounded-2xl bg-red-950/15 border border-red-500/20">
              <span className="px-2.5 py-1 rounded bg-red-500/20 border border-red-500/30 text-red-300 font-mono text-xs uppercase font-bold inline-block mb-4">
                Before: Founder-Led Operations
              </span>
              <div className="space-y-3 text-sm text-slate-300">
                <div className="flex items-start gap-2.5">
                  <span className="text-red-400 font-mono">•</span>
                  <span>Founder managed SEO, training cohorts, content, emails, and doubts single-handedly</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="text-red-400 font-mono">•</span>
                  <span>High organizational dependency on a single person</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="text-red-400 font-mono">•</span>
                  <span>General SEO traffic not aligned with course enrollment intent</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="text-red-400 font-mono">•</span>
                  <span>Higher student drop-off and refund requests due to delayed doubt support</span>
                </div>
              </div>
            </div>

            {/* After Card */}
            <div className="p-6 rounded-2xl bg-emerald-950/20 border border-emerald-500/30">
              <span className="px-2.5 py-1 rounded bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-mono text-xs uppercase font-bold inline-block mb-4">
                After: Scalable System &amp; Leadership
              </span>
              <div className="space-y-3 text-sm text-slate-200">
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>Structured, intent-driven SEO strategy with technical health</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>Dedicated Content Writer &amp; SEO Intern operating with defined SOPs</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>Defined 4-pillar curriculum and 2 live masterclasses weekly</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>Automated ConvertKit email nurture sequences based on user actions</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span><strong>-60% refunds</strong> + <strong>+35% intent traffic</strong> + founder freed to scale business</span>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* SECTION 7: CORE IMPACT */}
        <section id="core-impact" className="scroll-mt-24 mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold">
              07 / The Core Impact
            </h2>
          </div>

          <h3 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-4">
            Beyond Traffic: Building Systems That Scale
          </h3>
          <p className="text-base text-slate-300 leading-relaxed mb-6">
            My journey at <strong className="text-white">Workhours (now Trydo School)</strong> evolved from an SEO engagement into a complete <strong className="text-cyan-300">Digital Marketing, Education, and Growth role</strong>.
          </p>

          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 text-sm leading-relaxed text-slate-300 space-y-4">
            <p>
              The objective was never simply to chase vanity traffic counters on a dashboard. It was about attracting the <strong>right prospective students</strong>, creating a fulfilling and structured educational experience, nurturing them with timely automated guidance, and drastically reducing operational dependency on the founder.
            </p>
            <div className="p-4 rounded-xl bg-[#050811] border border-white/5 font-mono text-xs text-cyan-300 text-center">
              Intent-Driven Traffic ➔ Structured Curriculum ➔ Live Mentorship ➔ Automated Nurture ➔ Scalable Growth
            </div>
            <p>
              The frameworks and systems established during this tenure allowed the institute to scale smoothly into what is now Trydo School, proving that long-term SEO success must always connect directly to customer retention and business outcomes.
            </p>
          </div>
        </section>

        {/* EXPLORE OTHER CASE STUDIES */}
        <section className="mb-14 pt-8 border-t border-white/[0.08]">
          <h4 className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-4 font-semibold">
            Explore Other Case Studies
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            
            <Link
              to="/casestudy/seismic/"
              className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-cyan-500/40 transition-all group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between text-xs font-mono text-cyan-400 mb-2">
                  <span>Seismic Software</span>
                  <span className="px-2 py-0.5 rounded bg-cyan-400/10 border border-cyan-400/20 text-[10px]">Enterprise CMS</span>
                </div>
                <h5 className="font-heading font-bold text-white text-base group-hover:text-cyan-300 transition-colors mb-2">
                  WordPress to Sitecore Migration &amp; AI-Search Lift
                </h5>
                <p className="text-xs text-slate-400 line-clamp-2">
                  How I stopped an initial 20% traffic drop, fully recovered rankings in 90 days, and lifted AI citations by 120%.
                </p>
              </div>
              <div className="mt-4 flex items-center gap-1.5 text-xs font-mono text-cyan-400 font-semibold">
                <span>Read Case Study</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/casestudy/myoperator/"
              className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-indigo-500/40 transition-all group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between text-xs font-mono text-indigo-400 mb-2">
                  <span>MyOperator</span>
                  <span className="px-2 py-0.5 rounded bg-indigo-400/10 border border-indigo-400/20 text-[10px]">B2B SaaS</span>
                </div>
                <h5 className="font-heading font-bold text-white text-base group-hover:text-indigo-300 transition-colors mb-2">
                  200% Organic Surge &amp; 60% Pipeline Sourced
                </h5>
                <p className="text-xs text-slate-400 line-clamp-2">
                  Rebuilding SaaS content architecture, pruning low-value URLs, and turning organic search into the #1 revenue channel.
                </p>
              </div>
              <div className="mt-4 flex items-center gap-1.5 text-xs font-mono text-indigo-400 font-semibold">
                <span>Read Case Study</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

          </div>
        </section>

        {/* AUTHOR BIO & CTA CARD */}
        <div className="p-6 sm:p-8 rounded-3xl bg-gradient-to-b from-[#0e1626] to-[#080c16] border border-white/10 shadow-2xl flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4 text-left">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 via-indigo-600 to-cyan-400 text-white flex items-center justify-center font-bold text-lg flex-shrink-0 shadow-lg shadow-blue-500/20">
              KN
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-heading font-bold text-lg text-white">Kanna Narendra Kumar</h4>
                <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                  Open for Work
                </span>
              </div>
              <p className="text-xs sm:text-sm text-slate-400 mt-1 max-w-md">
                Senior SEO Specialist &amp; AI Search Strategist with 8+ years scaling organic pipeline, traffic, and high-impact educational growth.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-start md:justify-end">
            <Link
              to="/#case-studies"
              className="px-4 py-2.5 rounded-xl bg-white/[0.06] hover:bg-white/[0.12] text-xs font-mono text-white transition-colors border border-white/5 flex items-center gap-2"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to Portfolio</span>
            </Link>
            <a
              href="mailto:narendrakumarkanna@gmail.com"
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-xs font-mono text-white font-semibold transition-all shadow-md shadow-blue-500/20 flex items-center gap-2"
            >
              <span>Contact Me</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

      </main>

      {/* Simple Footer */}
      <footer className="border-t border-white/[0.08] py-10 px-4 text-center text-xs font-mono text-slate-500">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {new Date().getFullYear()} Kanna Narendra Kumar · narendradigital.in</p>
          <div className="flex items-center gap-4 text-[11px] text-slate-400">
            <Link to="/" className="hover:text-white transition-colors">Portfolio Home</Link>
            <span>•</span>
            <a href="mailto:narendrakumarkanna@gmail.com" className="hover:text-white transition-colors">Contact</a>
            <span>•</span>
            <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="hover:text-white transition-colors cursor-pointer">
              Back to Top ↑
            </button>
          </div>
        </div>
      </footer>

    </div>
  );
}
